import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import NavigationService from '../../../services/navigationService';
import buddiesService from '../services/buddiesService';
import { notificationsService } from '../../../services/photoEngagementService';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YDialog } from '../../../ui/dialog/YDialog';
import {
  FaBroom,
  FaUserPlus,
  FaSearch,
  FaCheck,
  FaTimes,
} from 'react-icons/fa';
import safeStorage from '../../../utils/safeStorage';
import { BuddyProfileCard } from './BuddyProfileCard';
import api from '../../../utils/api';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useInTheStandCount } from '../../../hooks/useInTheStandCount';
import { CommunityShellTabs } from '../../community/components/CommunityShellTabs';
import { CachedImage } from '../../../components/common/CachedImage';
import { getCachedBuddiesHub, isCachedBuddiesHubFresh, setCachedBuddiesHub } from '../../../utils/buddiesCache';
import { buddyProfilesCoverList, countSharedClubs, mergeBuddyListWithProfiles } from '../utils/buddyListSeed';
import './hunting-buddies-shell.css';

const HuntingBuddiesPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { count: inTheWoodsCount } = useInTheStandCount();
  const requestsRef = useRef(null);
  const cachedHub = getCachedBuddiesHub({ allowStale: true });
  const [buddies, setBuddies] = useState(() => cachedHub?.buddies || []);
  const [requests, setRequests] = useState(
    () => cachedHub?.requests || { incoming: [], outgoing: [] }
  );
  const [buddyFilterQ, setBuddyFilterQ] = useState('');
  const [huntingNowOnly, setHuntingNowOnly] = useState(false);
  const [findDialogOpen, setFindDialogOpen] = useState(false);
  const [findQ, setFindQ] = useState('');
  const [findResults, setFindResults] = useState([]);
  const [findLoading, setFindLoading] = useState(false);
  const findDebounceRef = useRef(null);
  const [buddyActivity, setBuddyActivity] = useState({ results: [], count: 0, next: null });
  const [buddiesWithProfiles, setBuddiesWithProfiles] = useState(
    () => cachedHub?.buddiesWithProfiles || []
  );
  const [selfProfile, setSelfProfile] = useState(null);
  const [selfHandle, setSelfHandle] = useState('');

  const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day === 1) return 'yesterday';
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const load = useCallback(async () => {
    try {
      const [b, r] = await Promise.all([
        buddiesService.listBuddies(),
        buddiesService.listRequests(),
      ]);
      setBuddies(b);
      setRequests(r);
      const cacheWasFresh = isCachedBuddiesHubFresh();
      const previous = getCachedBuddiesHub({ allowStale: true })?.buddiesWithProfiles || [];
      const merged = mergeBuddyListWithProfiles(b, previous);
      setBuddiesWithProfiles(merged);
      setCachedBuddiesHub({ buddies: b, requests: r, buddiesWithProfiles: merged });
      if (cacheWasFresh && buddyProfilesCoverList(b, previous)) {
        return;
      }

      const profilesPromises = b.map(async (buddy) => {
        try {
          const resp = await api.get(`/users/${buddy.id}/profile/`);
          const profileData = resp.data?.data || {};
          return {
            ...buddy,
            profile: {
              ...buddy.profile,
              ...profileData,
              current_sign_out: profileData.current_sign_out,
              last_sign_out: profileData.last_sign_out,
              clubs_summary: profileData.clubs_summary || buddy.profile?.clubs_summary || [],
              preferred_hunting_method: profileData.preferred_hunting_method,
              club_roles: profileData.club_roles || [],
              location_label: profileData.location_label || profileData.home_state || null,
            },
          };
        } catch (e) {
          return buddy;
        }
      });

      const buddiesWithData = await Promise.all(profilesPromises);
      setBuddiesWithProfiles(buddiesWithData);
      setCachedBuddiesHub({ buddies: b, requests: r, buddiesWithProfiles: buddiesWithData });
    } catch (e) {
      if (!getCachedBuddiesHub({ allowStale: true })) {
        setBuddies([]);
        setRequests({ incoming: [], outgoing: [] });
        setBuddiesWithProfiles([]);
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/profiles/me/');
        setSelfProfile(res?.data || null);
      } catch (e) {
        setSelfProfile(null);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!currentUser?.id) return;
        const resp = await api.get(`/users/${currentUser.id}/profile/`);
        setSelfHandle(resp?.data?.data?.handle || `@${currentUser.username}`);
      } catch (e) {
        setSelfHandle(currentUser?.username ? `@${currentUser.username}` : '@me');
      }
    })();
  }, [currentUser?.id, currentUser?.username]);

  const runFindSearch = useCallback(async () => {
    if (!findQ || findQ.trim().length < 2) {
      setFindResults([]);
      return;
    }
    setFindLoading(true);
    try {
      const items = await buddiesService.searchUsers(findQ.trim());
      setFindResults(items);
    } catch (e) {
      setFindResults([]);
    } finally {
      setFindLoading(false);
    }
  }, [findQ]);

  useEffect(() => {
    if (!findDialogOpen) return undefined;
    if (findDebounceRef.current) clearTimeout(findDebounceRef.current);
    findDebounceRef.current = setTimeout(() => { runFindSearch(); }, 300);
    return () => {
      if (findDebounceRef.current) clearTimeout(findDebounceRef.current);
    };
  }, [findQ, findDialogOpen, runFindSearch]);

  useEffect(() => {
    (async () => {
      try {
        const data = await notificationsService.list(1);
        const clearedAt = Number(safeStorage.getItem('buddyActivityClearedAt') || 0);
        const filtered = (data?.results || []).filter((n) => {
          const ts = n?.created_at ? Date.parse(n.created_at) : Date.now();
          return ts > clearedAt;
        });
        setBuddyActivity({ ...(data || {}), results: filtered });
      } catch (e) {
        setBuddyActivity({ results: [] });
      }
    })();
  }, []);

  const sendRequest = async (userId) => {
    await buddiesService.sendRequest(userId);
    await load();
    await runFindSearch();
  };

  const accept = async (reqId) => { await buddiesService.acceptRequest(reqId); await load(); };
  const decline = async (reqId) => { await buddiesService.declineRequest(reqId); await load(); };

  const activeBuddies = inTheWoodsCount;
  const totalBuddies = buddies.length;

  const sharedClubsCount = useMemo(
    () => countSharedClubs(buddiesWithProfiles),
    [buddiesWithProfiles]
  );

  const incomingCount = requests.incoming?.length || 0;
  const hasRequests = incomingCount > 0 || (requests.outgoing?.length || 0) > 0;

  const filteredBuddies = useMemo(() => {
    const q = buddyFilterQ.trim().toLowerCase();
    return buddiesWithProfiles.filter((buddy) => {
      if (huntingNowOnly && !buddy.profile?.current_sign_out) return false;
      if (!q) return true;
      const name = `${buddy.first_name || ''} ${buddy.last_name || ''} ${buddy.username || ''}`.toLowerCase();
      const handle = (buddy.profile?.handle || buddy.handle || '').toLowerCase();
      const clubs = (buddy.profile?.clubs_summary || []).join(' ').toLowerCase();
      return name.includes(q) || handle.includes(q) || clubs.includes(q);
    });
  }, [buddiesWithProfiles, buddyFilterQ, huntingNowOnly]);

  const clearActivity = () => {
    try {
      safeStorage.setItem('buddyActivityClearedAt', String(Date.now()));
    } catch (e) { /* ignore */ }
    setBuddyActivity({ results: [], count: 0, next: null });
  };

  const openFindDialog = () => {
    setFindDialogOpen(true);
    setFindQ('');
    setFindResults([]);
  };

  const scrollToRequests = () => {
    if (!hasRequests) return;
    requestsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const initialsFrom = (name, fallback = '?') => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return fallback;
  };

  const renderSelfCard = useCallback(() => {
    if (!currentUser) return null;
    const handle = selfHandle || (currentUser.username ? `@${currentUser.username}` : '@me');
    const photoUrl = selfProfile?.photo_url;
    const displayName = 'You';
    const init = initialsFrom(
      `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username,
      'ME',
    );

    return (
      <div
        className="y-buddy-card y-buddy-card--field y-buddy-card-self"
        data-testid="hb-self-card"
        onClick={() => navigate(`/users/${currentUser.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/users/${currentUser.id}`);
          }
        }}
      >
        <div className="y-buddy-card__head">
          <span className="y-buddy-card__avatar-wrap">
            {photoUrl ? (
              <CachedImage src={photoUrl} alt={handle} className="y-buddy-card__avatar" loading="lazy" decoding="async" />
            ) : (
              <span className="y-buddy-card__avatar y-buddy-card__avatar--placeholder" aria-hidden="true">
                {init}
              </span>
            )}
          </span>
          <span className="y-buddy-card__identity">
            <span className="y-buddy-card__name">{displayName}</span>
            <span className="y-buddy-card__handle">{handle.startsWith('@') ? handle : `@${handle}`}</span>
          </span>
          <span className="y-buddy-card__chip">YOU</span>
        </div>
        <div className="y-buddy-card__body">
          <p className="y-buddy-card__line">Your profile · shared clubs and sits</p>
          <div className="y-buddy-card__actions">
            <YButton
              size="sm"
              variant="secondary"
              className="y-buddy-card__btn"
              onClick={(e) => {
                e?.stopPropagation();
                navigate(`/users/${currentUser.id}`);
              }}
            >
              PROFILE
            </YButton>
          </div>
        </div>
      </div>
    );
  }, [currentUser, navigate, selfHandle, selfProfile]);

  const activityInitials = (n) => {
    const name =
      `${n.actor?.profile?.first_name || n.actor?.first_name || ''} ${n.actor?.profile?.last_name || n.actor?.last_name || ''}`.trim()
      || n.actor?.username
      || '?';
    return initialsFrom(name);
  };

  const activityPhotoUrl = (n) =>
    n.actor?.profile?.photo_url || n.actor?.photo_url || null;

  const renderActivityItem = (n) => {
    const isHuntOut = n.type === 'hunt_sign_out';
    const isHuntIn = n.type === 'hunt_sign_in';

    const go = () => {
      if (n.type === 'status_update') {
        navigate(`/users/${n.actor?.id}`);
      } else if (n.type === 'photo_posted' || n.type === 'photo_comment') {
        const ownerId = n.owner_user_id || n.actor?.id;
        navigate(`/users/${ownerId}`, { state: { openHarvestId: n.harvest_id } });
      } else if (isHuntOut || isHuntIn) {
        navigate(`/users/${n.actor?.id}`);
      }
    };

    let text;
    const actorLabel =
      n.actor?.profile?.first_name
      || n.actor?.first_name
      || n.actor?.username
      || 'Buddy';
    if (isHuntOut) {
      text = `${actorLabel} tagged out${n.time_period ? ` for the ${n.time_period} sit` : ''}.`;
    } else if (isHuntIn) {
      text = `${actorLabel} signed back in.`;
    } else if (n.type === 'status_update') {
      text = `${actorLabel} updated status.`;
    } else if (n.type === 'bio_update') {
      text = `${actorLabel} updated bio.`;
    } else if (n.type === 'photo_posted') {
      text = `${actorLabel} put up a photo.`;
    } else if (n.type === 'photo_comment') {
      text = `${actorLabel} commented on a photo.`;
    } else {
      text = n.text || 'Buddy activity';
    }

    const when = formatRelative(n.created_at).toUpperCase();
    const photoUrl = activityPhotoUrl(n);

    return (
      <div
        key={n.id}
        className="hb-activity-item"
        onClick={go}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            go();
          }
        }}
      >
        <span className="hb-activity-item__avatar" aria-hidden="true">
          {photoUrl ? (
            <CachedImage src={photoUrl} alt="" />
          ) : (
            activityInitials(n)
          )}
        </span>
        <span className="hb-activity-item__copy">
          <span className="hb-activity-item__text">{text}</span>
          {n.text && n.type !== 'status_update' && !isHuntOut && !isHuntIn ? (
            <span className="hb-activity-item__extra">{n.text}</span>
          ) : null}
          <span className="hb-activity-item__when">{when}</span>
        </span>
        {!!n.club_id && (
          <YButton
            size="sm"
            variant="secondary"
            className="hb-activity-item__map"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/clubs/${n.club_id}/map`);
            }}
          >
            MAP
          </YButton>
        )}
      </div>
    );
  };

  const requestUserInitials = (user) => {
    const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || '?';
    return initialsFrom(name);
  };

  const renderIncomingRequest = (r) => {
    const from = r.from_user || {};
    const mutual = r.mutual_buddies_count || 0;
    return (
      <div key={r.id} className="hb-request-card" data-testid="hb-incoming-request">
        <div className="hb-request-card__user">
          <div className="hb-request-card__avatar">
            {from.photo_url ? <CachedImage src={from.photo_url} alt="" /> : requestUserInitials(from)}
          </div>
          <div>
            <p className="hb-request-card__name">{from.first_name || from.username}</p>
            <p className="hb-request-card__handle">{from.handle || `@${from.username}`}</p>
            <div className="hb-request-card__badges">
              {mutual > 0 && (
                <span className="hb-request-badge hb-request-badge--mutual">
                  {mutual}
                  {' '}
                  mutual
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="hb-request-card__actions">
          <YButton size="sm" variant="primary" className="gap-2" onClick={() => accept(r.id)}>
            <FaCheck aria-hidden="true" />
            Accept
          </YButton>
          <YButton size="sm" variant="secondary" onClick={() => decline(r.id)}>
            <FaTimes aria-hidden="true" />
            Decline
          </YButton>
        </div>
      </div>
    );
  };

  const renderOutgoingRequest = (r) => {
    const to = r.to_user || {};
    const mutual = r.mutual_buddies_count || 0;
    return (
      <div key={r.id} className="hb-request-card" data-testid="hb-outgoing-request">
        <div className="hb-request-card__user">
          <div className="hb-request-card__avatar">
            {to.photo_url ? <CachedImage src={to.photo_url} alt="" /> : requestUserInitials(to)}
          </div>
          <div>
            <p className="hb-request-card__name">{to.first_name || to.username || 'User'}</p>
            <p className="hb-request-card__handle">{to.handle || (to.username ? `@${to.username}` : '')}</p>
            <div className="hb-request-card__badges">
              <span className="hb-request-badge">Pending</span>
              {mutual > 0 && (
                <span className="hb-request-badge hb-request-badge--mutual">
                  {mutual}
                  {' '}
                  mutual
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="hb-shell hb-shell--field" data-testid="hunting-buddies-page">
      <CommunityShellTabs />

      <div className="hb-inner">
        <div className="hb-stats y-carousel y-carousel--stat-4" data-testid="hb-stats">
          <div className="hb-stat">
            <div className="hb-stat__label">Total Buddies</div>
            <div className="hb-stat__value" data-testid="hb-stat-total">{totalBuddies}</div>
          </div>
          <div className="hb-stat hb-stat--accent">
            <div className="hb-stat__label">In the Woods</div>
            <div className="hb-stat__value" data-testid="hb-stat-woods">{activeBuddies}</div>
          </div>
          <div className="hb-stat">
            <div className="hb-stat__label">Shared Clubs</div>
            <div className="hb-stat__value" data-testid="hb-stat-clubs">{sharedClubsCount}</div>
          </div>
          <button
            type="button"
            className={`hb-stat hb-stat--pending${incomingCount > 0 ? ' is-hot' : ''}`}
            data-testid="hb-pending-stat"
            onClick={scrollToRequests}
            disabled={!hasRequests}
          >
            <div className="hb-stat__label">Pending</div>
            <div className="hb-stat__value" data-testid="hb-stat-pending">{incomingCount}</div>
          </button>
        </div>

        <div className="hb-toolbar" data-testid="hb-buddy-filter">
          <div className="hb-toolbar__search">
            <YInput
              id="buddy-filter-search"
              placeholder="Search buddies…"
              value={buddyFilterQ}
              onChange={(e) => setBuddyFilterQ(e.target.value)}
              aria-label="Search existing buddies"
            />
          </div>
          <YButton
            size="sm"
            variant={huntingNowOnly ? 'primary' : 'secondary'}
            className={`hb-toolbar__chip${huntingNowOnly ? ' is-on' : ''}`}
            data-testid="hb-hunting-now"
            onClick={() => setHuntingNowOnly((v) => !v)}
          >
            HUNTING NOW
          </YButton>
          <YButton
            size="sm"
            variant="primary"
            className="hb-toolbar__find"
            data-testid="hb-find-buddies-btn"
            onClick={openFindDialog}
          >
            + FIND BUDDIES
          </YButton>
        </div>

        <div data-testid="buddy-my-buddies-card">
          {(!currentUser && filteredBuddies.length === 0) ? (
            <div className="hb-empty">No buddies yet. Use Find Buddies to connect with hunters.</div>
          ) : (
            <div className="y-buddy-grid y-buddy-grid--field y-carousel">
              {renderSelfCard()}
              {filteredBuddies.map((buddy) => (
                <BuddyProfileCard
                  key={buddy.id}
                  buddy={buddy}
                  onMessage={(b) => NavigationService.navigateToUserMessage(
                    b.id,
                    b.username || b.first_name || '',
                    navigate,
                  )}
                />
              ))}
              {filteredBuddies.length === 0 && currentUser && (
                <div className="hb-empty" style={{ gridColumn: '1 / -1' }}>
                  {buddyFilterQ || huntingNowOnly
                    ? 'No buddies match your filters.'
                    : 'No buddies yet. Use Find Buddies to connect with hunters.'}
                </div>
              )}
            </div>
          )}
        </div>

        {hasRequests ? (
          <div
            className="hb-requests"
            ref={requestsRef}
            id="hb-requests"
            data-testid="hb-requests"
          >
            {incomingCount > 0 ? (
              <section className="hb-requests-section">
                <h2 className="hb-requests-section__title">
                  Incoming Requests
                  <span className="hb-tab__badge">{incomingCount}</span>
                </h2>
                {(requests.incoming || []).map(renderIncomingRequest)}
              </section>
            ) : null}
            {(requests.outgoing || []).length > 0 ? (
              <section className="hb-requests-section">
                <h2 className="hb-requests-section__title">Outgoing Requests</h2>
                {(requests.outgoing || []).map(renderOutgoingRequest)}
              </section>
            ) : null}
          </div>
        ) : null}

        <div className="hb-activity" data-testid="buddy-activity-card">
          <div className="hb-activity__head">
            <span className="hb-activity__title">Buddy Activity</span>
            <span className="hb-activity__meta">Last 7 Days</span>
            <YButton
              variant="ghost"
              size="sm"
              className="hb-activity__clear"
              title="Clear"
              aria-label="Clear activity"
              onClick={clearActivity}
            >
              <FaBroom />
            </YButton>
          </div>
          {(!buddyActivity.results || buddyActivity.results.length === 0) ? (
            <div className="hb-activity__empty">No recent activity from your buddies</div>
          ) : (
            <div className="hb-activity-list">
              {buddyActivity.results.slice(0, 50).map(renderActivityItem)}
            </div>
          )}
        </div>
      </div>

      <YDialog
        show={findDialogOpen}
        onHide={() => setFindDialogOpen(false)}
        title={(
          <span className="d-inline-flex align-items-center gap-2">
            <FaUserPlus aria-hidden="true" />
            Find Hunting Buddies
          </span>
        )}
        size="md"
        testId="hb-find-buddies-dialog"
      >
        <div className="position-relative mb-3">
          <FaSearch
            className="position-absolute text-muted"
            style={{ left: 12, top: '50%', transform: 'translateY(-50%)' }}
            aria-hidden="true"
          />
          <YInput
            id="hb-find-search"
            placeholder="Search by name or handle…"
            value={findQ}
            onChange={(e) => setFindQ(e.target.value)}
            style={{ paddingLeft: '2.25rem' }}
            autoFocus
            data-testid="hb-find-search-input"
          />
        </div>
        {findLoading ? (
          <div className="text-muted small">Searching…</div>
        ) : findResults.length > 0 ? (
          <div className="hb-find-results">
            {findResults.map((u) => (
              <div key={u.id} className="hb-find-row">
                <div className="d-flex align-items-center gap-2 min-w-0">
                  {u.photo_url && (
                    <CachedImage
                      src={u.photo_url}
                      alt=""
                      style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  )}
                  <div className="min-w-0">
                    <div className="fw-semibold">{u.first_name || u.username}</div>
                    <div className="text-muted small">{u.handle}</div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  {u.relationship_status && u.relationship_status !== 'none' ? (
                    <Badge bg={u.relationship_status.startsWith('pending') ? 'warning' : 'success'}>
                      {u.relationship_status.replace('_', ' ')}
                    </Badge>
                  ) : (
                    <YButton size="sm" variant="primary" onClick={() => sendRequest(u.id)}>
                      Add
                    </YButton>
                  )}
                  {u.relationship_status === 'accepted' && (
                    <YButton
                      size="sm"
                      variant="secondary"
                      onClick={() => NavigationService.navigateToUserMessage(
                        u.id,
                        u.username || u.first_name || '',
                        navigate,
                      )}
                    >
                      Message
                    </YButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : findQ.trim().length >= 2 ? (
          <div className="hb-empty">No hunters found matching your search</div>
        ) : (
          <div className="hb-empty">Start typing to search for hunters</div>
        )}
      </YDialog>
    </div>
  );
};

export default HuntingBuddiesPage;
